/**
 * One line of the activity feed, assembled from the four vocabularies `nb.json` provides.
 *
 * `ActivityEntryDto` carries structure, never prose — an action, a physical table name, the label
 * the object had at the time, and the NAMES of the fields an update touched. The API never
 * returns a translated sentence (CLAUDE.md, language rules), so the sentence is built here out of
 * four parts, each of which is a key rather than a string:
 *
 *   `activity.line.<action>`      the sentence, with a `_noLabel` variant
 *   `entities.<entityType>`       the physical table in definite form — "oppgaven"
 *   `fields.<camelCase>`          a changed column's name — "frist", "ansvarlig"
 *   `enums.audit_action.<action>` the bare verb, as the last resort below
 *
 * All four prefixes are declared in `i18n/dynamic-keys.ts`, which is what tells `check-i18n` that
 * a key nobody spells out in source is assembled rather than dead.
 *
 * ---------------------------------------------------------------------------------------------
 * WHY EVERY LOOKUP CARRIES A `defaultValue`.
 *
 * `entityType` is TEXT in the contract and `changedFields` is an open list of column names, both
 * on purpose: "a Phase 2 table becomes auditable without a migration". So the vocabularies are
 * deliberately not exhaustive, and the failure mode of a missing key is a raw dotted path on an
 * admin's dashboard — `fields.curfewAt` sitting in a Norwegian sentence. `humanise()` turns the
 * identifier the data already carries into a word, so a column nobody has translated yet reads
 * as "curfew at" rather than as a bug. It is a floor, not a translation: the key still belongs in
 * `nb.json`, and this is what stands there until it arrives.
 *
 * `activity.line.*` covers all thirteen members of `AuditAction` today, so its default is
 * unreachable — until somebody extends the enum, which is a migration and not a locale change.
 * Its fallback is the bare verb from `enums.audit_action.*`, which is the one part of the
 * sentence that is guaranteed to exist for an action that exists.
 * ---------------------------------------------------------------------------------------------
 *
 * WHAT THIS LINE DOES NOT DO: LINK TO A THREAD. `entities.threads` is in the vocabulary and
 * `/inbox/$threadId` is a real route, and it is still not a link. `InboxService` INNER JOINs the
 * caller's own participant row (contract C-43), so an admin reading the audit log of a
 * conversation they are not in would follow that link to a 403. A dead link is worse than plain
 * text. Only `tasks` and `contacts` are linked: both are org-wide readable for the two roles that
 * can see this panel at all, and both survive the click.
 *
 * A `delete` is never a link either, whatever the table. The row is soft-deleted, the id still
 * resolves in the audit log, and the page behind it does not.
 */
import type { ActivityActorDto, ActivityEntryDto, Locale, MeResponse } from '@tvitvi/shared';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { useMe } from '@/api/queries/me';
import { hasModule } from '@/auth/organization-modules';
import { actionLabel } from '@/features/settings/permission-texts';
import { currentLocale } from '@/i18n';
import { dynamicKey } from '@/i18n/types';
import { formatCalendarDate, formatDateTime, formatList } from '@/lib/format';

/**
 * `assigneePersonId` → `assignee person id`; `contact_tags` → `contact tags`.
 *
 * English, because the identifier is: the whole point is that it is a legible word instead of a
 * dotted key, not that it is a translation. Anything that should read as Norwegian gets a key.
 */
export function humanise(identifier: string): string {
  return identifier
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Who acted.
 *
 * `displayName` is null in two different situations and they need two different words. A `system`
 * or `service` actor never had a name — a retention sweep, a future import — and is "Systemet". A
 * `user` actor whose contact card was soft-deleted did, and is "Ukjent": the entry stays in the
 * feed, because history does not disappear when somebody leaves the organization.
 */
export function useActorName(): (actor: ActivityActorDto) => string {
  const { t } = useTranslation();
  return (actor) => {
    const name =
      actor.displayName ??
      (actor.type === 'user' ? t('activity.unknownActor') : t('activity.systemActor'));
    /* A11 (1031): plattformadmin inne som support står med navnet og «(support)». */
    const shown = actor.isSupport === true ? t('activity.supportActor', { name }) : name;
    /* B3: gjort med et token utenfra — «Atle Aalstad via Claude». */
    return actor.client === undefined
      ? shown
      : t('activity.viaClient', { name: shown, client: actor.client.name });
  };
}

/** The entity types with a route behind them that the panel's two roles can actually open. */
const LINKED_ENTITIES = ['tasks', 'contacts'] as const;
type LinkedEntity = (typeof LINKED_ENTITIES)[number];

function linkTargetOf(entry: ActivityEntryDto): LinkedEntity | null {
  if (entry.action === 'delete') return null;
  return LINKED_ENTITIES.find((name) => name === entry.entityType) ?? null;
}

/**
 * The sentence itself, out of the four vocabularies — without the link, the date or the actor
 * lookup around it.
 *
 * EXTRACTED BECAUSE A SECOND FEED EXISTS. The booking's Oversikt tab shows the same kind of rows
 * from a different DTO (`OverviewActivity` carries `actorName` as a plain string where this one
 * carries an actor object), and it had assembled its own sentence from
 * `activity.action.<action>` — a key namespace that does not exist. Every default fell through,
 * so the largest panel on the most-visited page printed «delete personen på bookingen» in English
 * enum values. One builder, one vocabulary, and a namespace that is wrong is wrong in one place.
 */
/**
 * ORD SOM FØLGER MODULENE (regel 4, QA16 funn 38). Tabellen er den samme, ordet er det organisasjonen
 * bruker: et spillested uten Festival kaller `departments` grupper, og et spillested uten Konserter
 * kaller `concerts` (kveldens speil hos huset) arrangementer. Aldri organisasjonstypen.
 */
export interface ActivityWords {
  groups: boolean;
  events: boolean;
}

export function activityWordsOf(me: MeResponse | null): ActivityWords {
  return {
    groups: hasModule(me, 'venue') && !hasModule(me, 'festival'),
    events: hasModule(me, 'venue') && !hasModule(me, 'concerts'),
  };
}

const MODULE_WORDS: Readonly<Record<string, readonly [keyof ActivityWords, string]>> = {
  departments: ['groups', 'activity.entity.group'],
  resource_departments: ['groups', 'activity.entity.groupMembership'],
  concerts: ['events', 'activity.entity.event'],
  /* Arrangementets utøver heter «innslag» hos et spillested (QA15-S), ikke «booking». */
  performances: ['events', 'activity.entity.act'],
};

export function activitySentence(
  t: TFunction,
  entry: { action: string; entityType: string; entityLabel: string | null },
  actor: string,
  words?: ActivityWords,
): string {
  const override = MODULE_WORDS[entry.entityType];
  const entity =
    override !== undefined && words?.[override[0]] === true
      ? t(dynamicKey(override[1]))
      : t(dynamicKey(`entities.${entry.entityType}`), {
          defaultValue: humanise(entry.entityType),
        });
  const verb = t(dynamicKey(`enums.audit_action.${entry.action}`), {
    defaultValue: humanise(entry.action),
  });

  // `messages` has no label by design — the body is redacted out of the audit snapshot so an
  // admin cannot read a thread they are not in through the change log — so the `_noLabel`
  // variant is the normal case there rather than the exception.
  const lineKey = entry.entityLabel === null ? `${entry.action}_noLabel` : entry.action;
  return t(dynamicKey(`activity.line.${lineKey}`), {
    actor,
    entity,
    label: labelFor(t, entry.entityType, entry.entityLabel),
    defaultValue: verb,
  });
}

/**
 * RETTIGHETENS NAVN, IKKE NØKKELEN (QA5-B). Revisjonsraden for `role_permissions` bærer
 * «member / contact:delete» — rollen og handlingen slik de står i matrisen. Den leses med de samme
 * ordene Rettigheter bruker: rollens navn og innstillingens navn («Slette kontakter»), eller for en
 * fast regel ressurs og verb («Kontakter — se liste»). Aldri koden.
 */
const PERMISSION_LABEL = /^([a-z_]+) \/ ([a-z_]+:[a-z_]+)$/;

/** Hendelsene avtalearket førte som merkelapp før QA16 (funn 38): «proposed» sto rått i lista. */
const DEAL_SHEET_EVENTS = new Set(['proposed', 'amended', 'accepted', 'declined', 'withdrawn']);
const ISO_DAY = /\b(\d{4}-\d{2}-\d{2})\b/g;

function labelFor(t: TFunction, entityType: string, label: string | null): string {
  if (label === null) return '';
  if (entityType === 'deal_sheets' && DEAL_SHEET_EVENTS.has(label)) {
    return t(dynamicKey(`activity.dealSheetEvent.${label}`));
  }
  /* En dato i merkelappen («2027-05-15», «2027-05-15 · Havnehuset») leses som en dato (funn 38). */
  if (ISO_DAY.test(label)) {
    ISO_DAY.lastIndex = 0;
    return label.replace(ISO_DAY, (day) => formatCalendarDate(day, currentLocale()));
  }
  /* Kildens merkelapp er koden (`participation_sources.kind`): «travel_party» sto rått i lista. */
  if (entityType === 'participation_sources') {
    return t(dynamicKey(`enums.participation_source_kind.${label}`), {
      defaultValue: humanise(label),
    });
  }
  if (entityType !== 'role_permissions') return label;
  const match = PERMISSION_LABEL.exec(label);
  if (match === null) return label;
  const [, role = '', action = ''] = match;
  const roleName = t(dynamicKey(`enums.org_role.${role}`), { defaultValue: role });
  const question = actionLabel(t, action);
  return t('activity.permissionLabel', { role: roleName, permission: question });
}

/**
 * «SISTE AKTIVITET» KAN KLIKKES (F60): panelet som står på et spillested eller en årgang vet hvor
 * radene hører hjemme, og sier det her. Arrangementet, salen, forespørselen og husets innstillinger
 * på spillestedet; årgangens egen rad på festivalen. Uten konteksten står de som tekst, som før.
 */
export interface ActivityContext {
  /** Spillestedet panelet står på — `/venue/$festivalId/…`. */
  venueFestivalId?: string;
  /** Årgangen panelet står på. */
  festivalId?: string;
  editionId?: string;
}

export interface ActivityLineProps {
  entry: ActivityEntryDto;
  locale: Locale;
  context?: ActivityContext;
}

const LINK_CLASS = 'ring-focus hover:text-accent-text hover:underline';

export function ActivityLine({ entry, locale, context }: ActivityLineProps): ReactNode {
  const { t } = useTranslation();
  const actorName = useActorName();
  const me = useMe();

  const sentence = activitySentence(t, entry, actorName(entry.actor), activityWordsOf(me));

  const target = linkTargetOf(entry);
  const venue = entry.action === 'delete' ? undefined : context?.venueFestivalId;
  const festivalId = context?.festivalId;
  const editionId = context?.editionId;

  return (
    <>
      <span className="block">
        {venue !== undefined && entry.entityType === 'venue_nights' ? (
          <Link
            to="/venue/$festivalId/events/$nightId"
            params={{ festivalId: venue, nightId: entry.entityId }}
            className={LINK_CLASS}
            data-testid={`activity-link-${entry.id}`}
          >
            {sentence}
          </Link>
        ) : venue !== undefined && entry.entityType === 'venue_rooms' ? (
          <Link
            to="/venue/$festivalId/rooms/$roomId"
            params={{ festivalId: venue, roomId: entry.entityId }}
            search={{ section: 'spec' }}
            className={LINK_CLASS}
            data-testid={`activity-link-${entry.id}`}
          >
            {sentence}
          </Link>
        ) : venue !== undefined && entry.entityType === 'venue_enquiries' ? (
          <Link
            to="/venue/$festivalId/rooms"
            params={{ festivalId: venue }}
            search={{ section: 'requests', enquiry: entry.entityId }}
            className={LINK_CLASS}
            data-testid={`activity-link-${entry.id}`}
          >
            {sentence}
          </Link>
        ) : festivalId !== undefined &&
          editionId !== undefined &&
          entry.action !== 'delete' &&
          entry.entityType === 'editions' ? (
          <Link
            to="/festivals/$festivalId/editions/$editionId/settings"
            params={{ festivalId, editionId }}
            className={LINK_CLASS}
            data-testid={`activity-link-${entry.id}`}
          >
            {sentence}
          </Link>
        ) : target === 'tasks' ? (
          <Link
            to="/tasks/$taskId"
            params={{ taskId: entry.entityId }}
            className="ring-focus hover:text-accent-text hover:underline"
          >
            {sentence}
          </Link>
        ) : target === 'contacts' ? (
          <Link
            to="/contacts/$contactId"
            params={{ contactId: entry.entityId }}
            className="ring-focus hover:text-accent-text hover:underline"
          >
            {sentence}
          </Link>
        ) : (
          sentence
        )}
      </span>
      <ChangedFields fields={entry.changedFields} locale={locale} />
    </>
  );
}

/**
 * NÅR DET SKJEDDE (QA-runde 12, 4c) — ÉN GANG PER RAD (QA13 runde 3, blokk 2 a).
 *
 * Endringsloggen sa hvem som gjorde hva og ikke når. En logg uten tidspunkt er en liste over
 * påstander man ikke kan sette i rekkefølge — og rekkefølgen er hele grunnen til at noen åpner den.
 *
 * Absolutt tid og ikke «for 3 timer siden»: den som leser loggen gjør det som regel fordi noe skal
 * sammenlignes med noe annet — en e-post, en avtale, en annen logg — og da er en relativ tid et
 * regnestykke til. `<time>` bærer den maskinlesbare formen for den som kopierer den ut.
 *
 * ---------------------------------------------------------------------------------------------
 * HVORFOR DEN STÅR HER OG IKKE INNE I LINJA.
 *
 * Da 4c la tidspunktet inn i `ActivityLine`, hadde begge kallstedene alt sendt det samme tidspunktet
 * som `meta` på `PanelRow` — panelets egen konvensjon for «når», med tabellsifre i høyre kolonne.
 * Resultatet var to klokkeslett per rad, på Oversikt og på festivalsiden, i begge hus. Ingen av de
 * to visste om den andre, fordi den ene sto i raden og den andre i linja inni raden.
 *
 * Nå eier denne komponenten tidspunktet, og kallstedene sender DEN som `meta`. Det gir én
 * tidsangivelse, i kolonnen alle de andre panelene bruker, med `dateTime` i behold.
 */
export function ActivityWhen({
  occurredAt,
  locale,
}: {
  occurredAt: string;
  locale: Locale;
}): ReactNode {
  return (
    <time dateTime={occurredAt} data-testid="activity-when">
      {formatDateTime(occurredAt, locale)}
    </time>
  );
}

/**
 * "Endret: frist, ansvarlig og status" — the field NAMES an update touched, never their values.
 *
 * The values are not filtered out of a DTO here; they never left the database (see the header of
 * `packages/shared/src/contracts/activity.ts`). Names are structure and are safe; a value can be
 * a fee, a phone number or a message body, under a permission the page that owns it never
 * granted.
 *
 * Joined with `Intl.ListFormat` rather than `", "`, for the same reason nothing in this
 * application formats a date by concatenation: the separator before the last item is a property
 * of the language, and Norwegian and English do not agree about it.
 */
export function ChangedFields({
  fields,
  locale,
}: {
  fields: readonly string[];
  locale: Locale;
}): ReactNode {
  const { t } = useTranslation();
  if (fields.length === 0) return null;

  const names = fields.map((field) =>
    t(dynamicKey(`fields.${field}`), { defaultValue: humanise(field) }),
  );

  return (
    <span className="block text-xs text-subtle">
      {t('activity.changedFields', { fields: formatList(names, locale) })}
    </span>
  );
}
